// wave4-notif-conform: conforms the notification center to the legacy Notion spec (page
// d069715b "Notifications", Alex-and-Q UX board, "Web For Stabilization") --
// F:\AI_Data\projects\03-coowe-product\_planning\dev-fix\notification-center-archive-research-2026-07-02.md
// section 0 has the full spec text + mockups. Per QJ (functional bar, not pixel-match): bell near
// the logo with a visible "Notifications" label, a large overlay panel (not a small dropdown), a
// left category rail with per-category unread badges, "Mark all as read", unread rows visually
// distinct, click marks read + navigates. Styling is kept simple/clear rather than pixel-matched
// to the mockup.
//
// Supersedes the wave4-belllabel dropdown-panel version of this component (kept the sidebar
// icon+label row treatment from that commit, replaced the dropdown with the overlay panel below).
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react"

import moment from "moment-timezone"
import { connect } from "react-redux"
import { useHistory } from "react-router-dom"
import { bindActionCreators } from "redux"

import {
  loadNotificationCenter,
  loadMoreNotifications,
  markNotificationReadThunk,
  markAllNotificationsReadThunk,
} from "@actions/notificationActions"
import { setActiveTab } from "@actions/commonActions"
import {
  selectNotifications,
  selectUnreadCount,
  selectNotificationsHasMore,
  selectCategoryCounts,
  selectNotificationsLoading,
} from "@selectors/notification"
import { isDevBackend } from "@utils/devBackendShim"
import { SvgIcon } from "@components/common"
import { CATEGORIES, CATEGORY_ALL, categoryForType } from "@utils/notificationCategories"
import { highlightTypes } from "@config/enums"

import { Name } from "@components/NewSidebar/sidebarStyles"

import "./index.scss"

const POLL_MS = 20000

// NC3: per-type icon + copy so mention/nudge render distinctly from generic activity.
const TYPE_META = {
  mention: { icon: "🔔", label: "mentioned you" },
  reply: { icon: "💬", label: "replied to you" },
  nudge: { icon: "👉", label: "nudged you" },
  invite: { icon: "✉️", label: "invited you" },
  message: { icon: "💬", label: "sent a message" },
  decide: { icon: "✅", label: "made a decision" },
  reaction: { icon: "❤️", label: "reacted" },
  status: { icon: "🔄", label: "updated the TeeUp" },
  suggestion: { icon: "💡", label: "suggested something" },
  happening_soon: { icon: "⏰", label: "is starting soon" },
  happening: { icon: "▶️", label: "is happening now" },
  ended: { icon: "🏁", label: "has ended" },

  task_assigned: { icon: "🔔", label: "Task assigned" },
  task_claimed: { icon: "🔔", label: "Task claimed" },
  task_completed: { icon: "🔔", label: "Task completed" },
  task_due_soon: { icon: "🔔", label: "Task due soon" },
  reschedule_overdue_evaluated: { icon: "🔔", label: "Reschedule needed" },
  reschedule_proposed: { icon: "🔔", label: "Reschedule proposed" },
  removed: { icon: "🚪", label: "left the TeeUp" },
  join_request: { icon: "🙋", label: "requested to join" },
  connect_request: { icon: "🤝", label: "requested to connect" },
}
const DEFAULT_META = { icon: "🔔", label: "notified you" }

// NC-taproute: beta routed a notification tap to the in-TeeUp tab matching its type
// (mention -> Mentions, nudge -> Nudges, suggestion/reaction/decided -> game plan) instead of
// always landing on Chat. Everything ELSE (message, reply, status, the happening/ended lifecycle
// events, removed, join/connect requests, unknown types) goes to the TeeUp default Chat tab (0) --
// per the spec's "suggestion/reaction -> game plan; else -> that TeeUp". decide is included with
// the game-plan cluster because a decided notification is about the game plan (beta step 6 routes
// the gameplan-changed family to the gameplan view). `invite` needs no explicit tab: TeeUpPage
// auto-renders the invite preview when userStatus === invited, overriding whatever tab is active.
const TYPE_TAB_MAP = {
  mention: highlightTypes.mentions,
  nudge: highlightTypes.nudges,
  suggestion: highlightTypes.suggestions,
  reaction: highlightTypes.suggestions,
  decide: highlightTypes.suggestions,
}

const NotificationBell = ({
  notifications,
  unreadCount,
  hasMore,
  categoryCounts,
  loading,
  loadNotificationCenter,
  loadMoreNotifications,
  markNotificationReadThunk,
  markAllNotificationsReadThunk,
  setActiveTab,
  isSideBarOpen,
  variant,
}) => {
  const [open, setOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState(CATEGORY_ALL)
  const rootRef = useRef(null)
  const history = useHistory()

  useEffect(() => {
    if (!isDevBackend()) return undefined
    loadNotificationCenter()
    const id = setInterval(loadNotificationCenter, POLL_MS)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // wave4-notif-conform: opening the panel is a PURE UI toggle now -- no side effect on the
  // server or the badge. Legacy spec: badge = total unread, dropping only as items are actually
  // read (one at a time, or via the explicit "Mark all as read" button below). This REPLACES the
  // wave4-notif "mark-all-read-on-open" behavior.
  const handleToggle = useCallback(() => {
    setOpen(o => !o)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = e => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const handleItemClick = n => {
    if (!n.read) markNotificationReadThunk(n.id)
    setOpen(false)
    // legacy spec: "Click on the notification should mark this notification as read and open the
    // corresponding TeeUp" -- on the correct tab for the notification type (was always landing on
    // Chat). TeeUpPage resets activeTab to 0 on UNMOUNT of whatever TeeUp is currently open, so the
    // setActiveTab dispatch must run AFTER history.push, once that unmount cleanup has fired --
    // same ordering used at TeeupItem/index.js's onViewNudgeResponse.
    if (n.teeupId != null) {
      history.push(`/teeUps/${n.teeupId}`)
      const targetTab = TYPE_TAB_MAP[n.type] ?? highlightTypes.conversation
      setTimeout(() => setActiveTab(targetTab), 1)
    }
  }

  const visibleNotifications = useMemo(() => {
    if (activeCategory === CATEGORY_ALL) return notifications || []
    return (notifications || []).filter(n => categoryForType(n.type) === activeCategory)
  }, [notifications, activeCategory])

  if (!isDevBackend()) return null

  // wave4-nav-ia fix2: "logo" variant -- compact bell that sits ON the logo row itself (right
  // side), icon with the small "Notifications" label directly beneath it, per QJ's ratified
  // layout (nav-qj-layout.png). The default variant keeps the full sidebar-row treatment.
  const isLogoVariant = variant === "logo"

  return (
    <div
      className={
        "notification-bell" + (isLogoVariant ? " notification-bell--logo" : "")
      }
      ref={rootRef}
      data-testid="notification-bell">
      <button
        type="button"
        className={
          isLogoVariant
            ? "notification-bell__logo-trigger"
            : "notification-bell__trigger" +
              (isSideBarOpen ? "" : " notification-bell__trigger--collapsed")
        }
        data-testid="notification-bell-trigger"
        onClick={handleToggle}
        aria-label="Notifications">
        <span className="notification-bell__icon-wrap">
          <span className="notification-bell__icon">
            <SvgIcon name="bell" color="var(--gray6)" />
          </span>
          {unreadCount > 0 && (
            <span
              className="notification-bell__badge"
              data-testid="notification-bell-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>
        {/* QJ nav (2026-07-10, web-nav-3tab): the "Notifications" text label is REMOVED from the
            rail per QJ's target -- the bell is icon + unread badge only (top-right of the rail).
            The default (non-logo) variant keeps its labeled row for any other surface. */}
        {!isLogoVariant &&
          isSideBarOpen && (
            <Name data-testid="notification-bell-label">Notifications</Name>
          )}
      </button>
      {open && (
        <div
          className="notification-overlay"
          data-testid="notification-overlay"
          onClick={() => setOpen(false)}>
          <div
            className="notification-overlay__panel"
            data-testid="notification-bell-panel"
            onClick={e => e.stopPropagation()}>
            <div className="notification-overlay__header">
              <span className="notification-overlay__title">Notifications</span>
              <div className="notification-overlay__header-actions">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="notification-overlay__mark-all"
                    data-testid="notification-mark-all-read"
                    onClick={() => markAllNotificationsReadThunk()}>
                    Mark all as read
                  </button>
                )}
                <button
                  type="button"
                  className="notification-overlay__close"
                  data-testid="notification-overlay-close"
                  aria-label="Close notifications"
                  onClick={() => setOpen(false)}>
                  ✕
                </button>
              </div>
            </div>
            <div className="notification-overlay__body">
              <div className="notification-overlay__rail" data-testid="notification-category-rail">
                {CATEGORIES.map(cat => {
                  const count = categoryCounts[cat.id] || 0
                  return (
                    <button
                      type="button"
                      key={cat.id}
                      className={
                        "notification-overlay__rail-item" +
                        (activeCategory === cat.id
                          ? " notification-overlay__rail-item--active"
                          : "")
                      }
                      data-testid={`notification-category-${cat.id}`}
                      onClick={() => setActiveCategory(cat.id)}>
                      <span>{cat.label}</span>
                      {count > 0 && (
                        <span
                          className="notification-overlay__rail-badge"
                          data-testid={`notification-category-badge-${cat.id}`}>
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              <div className="notification-overlay__list" data-testid="notification-list">
                {visibleNotifications.length === 0 && !loading && (
                  <div className="notification-overlay__empty">No notifications</div>
                )}
                {visibleNotifications.map(n => {
                  const meta = TYPE_META[n.type] || DEFAULT_META
                  return (
                    <div
                      key={n.id}
                      data-testid={`notification-item-${n.type}`}
                      data-notif-type={n.type}
                      data-notif-read={n.read ? "true" : "false"}
                      className={
                        "notification-overlay__row" +
                        (n.read ? "" : " notification-overlay__row--unread")
                      }
                      onClick={() => handleItemClick(n)}>
                      <span className="notification-overlay__row-thumb" aria-hidden="true">
                        {n.actorAvatar ? (
                          <img src={n.actorAvatar} alt="" />
                        ) : (
                          <span className="notification-overlay__row-thumb-fallback">
                            {meta.icon}
                          </span>
                        )}
                      </span>
                      <div className="notification-overlay__row-body">
                        <span className="notification-overlay__row-title">
                          {n.actorName || "Someone"} {meta.label}
                        </span>
                        <span className="notification-overlay__row-copy">
                          {n.payload?.text || n.payload?.title || ""}
                        </span>
                      </div>
                      <span className="notification-overlay__row-time">
                        {n.createdAt ? moment(n.createdAt).fromNow() : ""}
                      </span>
                    </div>
                  )
                })}
                {activeCategory === CATEGORY_ALL && hasMore && (
                  <button
                    type="button"
                    className="notification-overlay__load-more"
                    data-testid="notification-load-more"
                    disabled={loading}
                    onClick={() => loadMoreNotifications(notifications.length)}>
                    {loading ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  notifications: selectNotifications(state),
  unreadCount: selectUnreadCount(state),
  hasMore: selectNotificationsHasMore(state),
  categoryCounts: selectCategoryCounts(state),
  loading: selectNotificationsLoading(state),
})

const mapDispatchToProps = dispatch => ({
  loadNotificationCenter: bindActionCreators(loadNotificationCenter, dispatch),
  loadMoreNotifications: bindActionCreators(loadMoreNotifications, dispatch),
  markNotificationReadThunk: bindActionCreators(markNotificationReadThunk, dispatch),
  markAllNotificationsReadThunk: bindActionCreators(
    markAllNotificationsReadThunk,
    dispatch
  ),
  setActiveTab: bindActionCreators(setActiveTab, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(NotificationBell)
